import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import AppLayout from '../components/work/AppLayout';
import { Button } from '../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { BrandEmpty, ListEmpty, LoadingList, WorkPageError } from '../components/work/page-states';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useOverlays } from '../components/work/overlay-context';
import { useWindowKeydown } from '../components/use-window-keydown';
import CreatePageDialog from '../components/wiki/CreatePageDialog';
import HistorySheet from '../components/wiki/HistorySheet';
import MovePageDialog from '../components/wiki/MovePageDialog';
import WikiPageEditor from '../components/wiki/WikiPageEditor';
import WikiPageView from '../components/wiki/WikiPageView';
import { MemberByline } from '../components/wiki/MemberByline';
import { useMembers } from '../components/wiki/use-member-names';
import { ApiError, apiErrorMessage } from '../lib/api';
import { compareText, matchesQuery } from '../lib/directory-filter';
import { formatTimestampDateTime } from '../lib/dates';
import { isProjectId } from '../lib/project-path';
import { isEditorTarget, type MemberDto } from '../lib/work-types';
import {
  deleteWikiPage,
  descendantIds,
  getWikiPageByPath,
  listWikiPages,
  listWikiSpaces,
  wikiRoute,
  type WikiPageFull,
  type WikiPageSlim,
  type WikiRevisionFull,
  type WikiSpaceRef,
} from '../lib/wiki';

type Props = { kind: 'org' | 'project' };

const SPACE_CHIPS = [] as const;
const SPACE_DEFAULT_ORDER = '-updated_at';
const SPACE_SORT_OPTIONS = [
  { value: '-updated_at', label: 'Recently updated' },
  { value: 'title', label: 'Title A-Z' },
  { value: '-title', label: 'Title Z-A' },
] as const;

/** Screens 2, 3, 4, 8: one component for the space home, page view and edit mode. */
export default function WikiSpace({ kind }: Props) {
  const params = useParams<{ projectId?: string; '*': string }>();
  const projectId = kind === 'project' ? params.projectId : undefined;
  if (kind === 'project' && !isProjectId(projectId)) {
    return <div className="p-6 text-sm text-muted-foreground">Missing stream.</div>;
  }
  const space: WikiSpaceRef = { kind, projectId: projectId ?? null };
  const path = (params['*'] ?? '').replace(/^\/+|\/+$/g, '');
  return <SpaceShell space={space} path={path} />;
}

function SpaceShell({ space, path }: { space: WikiSpaceRef; path: string }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const editing = searchParams.get('edit') === '1';
  const creating = searchParams.get('create') === '1';
  const { toast, confirm, isBlocked } = useOverlays();
  const members = useMembers();

  const [spaceName, setSpaceName] = useState<string>('Wiki');
  const [pages, setPages] = useState<WikiPageSlim[]>([]);
  const [pagesLoading, setPagesLoading] = useState(true);
  const [page, setPage] = useState<WikiPageFull | null>(null);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [createParent, setCreateParent] = useState<{ open: boolean; parent: WikiPageSlim | null }>({
    open: false,
    parent: null,
  });
  const [moveOpen, setMoveOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [restore, setRestore] = useState<WikiRevisionFull | null>(null);

  const spaceKey = `${space.kind}:${space.projectId ?? ''}`;

  const loadPages = useCallback(async () => {
    setError(null);
    try {
      const [rows, spaces] = await Promise.all([listWikiPages(space), listWikiSpaces()]);
      setPages(rows);
      const match = spaces.find((row) => row.kind === space.kind && (row.id ?? null) === (space.projectId ?? null));
      if (match) setSpaceName(space.kind === 'org' ? match.name : `${match.name} wiki`);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load wiki'));
      setPages([]);
    } finally {
      setPagesLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceKey]);

  useEffect(() => {
    setPagesLoading(true);
    void loadPages();
  }, [loadPages]);

  useEffect(() => {
    if (!path) {
      setPage(null);
      setPageLoading(false);
      return;
    }
    let cancelled = false;
    setPageLoading(true);
    getWikiPageByPath(space, path)
      .then((full) => {
        if (cancelled) return;
        setPage(full);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setPage(null);
        setError(
          err instanceof ApiError && err.status === 404 ? 'Page not found.' : apiErrorMessage(err, 'Failed to load page'),
        );
      })
      .finally(() => {
        if (!cancelled) setPageLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaceKey, path]);

  // Leaving edit mode drops any pending revision restore.
  useEffect(() => {
    if (!editing) setRestore(null);
  }, [editing]);

  const byId = useMemo(() => new Map(pages.map((row) => [row.id, row])), [pages]);
  const siblingsOf = useCallback(
    (parentId: string | null) => pages.filter((row) => row.parent_id === parentId),
    [pages],
  );

  function openCreate(parent: WikiPageSlim | null) {
    setCreateParent({ open: true, parent });
  }

  // `?create=1` (set by the sidebar's Create button) opens the dialog under the page being viewed.
  const currentPageId = page?.id ?? null;
  useEffect(() => {
    if (!creating || (path && !currentPageId)) return;
    setCreateParent({ open: true, parent: currentPageId ? byId.get(currentPageId) ?? null : null });
  }, [creating, path, currentPageId, byId]);

  // Single-key shortcuts while reading: E edits the open page, C creates a page under it (or at the root).
  useWindowKeydown((event) => {
    if (editing || isBlocked || error || pagesLoading || pageLoading || (path && !page)) return;
    if (event.defaultPrevented || event.repeat) return;
    if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
    if (isEditorTarget(event.target)) return;
    const key = event.key.toLowerCase();
    if (key === 'e' && page) {
      event.preventDefault();
      navigate(`${wikiRoute(space, page.path)}?edit=1`);
    } else if (key === 'c') {
      event.preventDefault();
      openCreate(page ? byId.get(page.id) ?? null : null);
    }
  });

  function closeCreate() {
    setCreateParent((c) => ({ ...c, open: false }));
    if (creating) {
      setSearchParams(
        (prev) => {
          prev.delete('create');
          return prev;
        },
        { replace: true },
      );
    }
  }

  async function copyLink() {
    if (!page) return;
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${wikiRoute(space, page.path)}`);
      toast('Link copied');
    } catch (err) {
      toast(apiErrorMessage(err, 'Clipboard is not available'));
    }
  }

  async function removePage() {
    if (!page) return;
    const count = descendantIds(pages, page.id).size - 1;
    const ok = await confirm({
      title: `Delete “${page.title}”?`,
      description:
        count > 0
          ? `This page and its ${count} child page${count === 1 ? '' : 's'} will be removed from the space.`
          : 'This page will be removed from the space.',
      confirmLabel: 'Delete',
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteWikiPage(space, page.id);
      toast('Page deleted');
      const parentPath = page.ancestors.map((row) => row.slug).join('/');
      navigate(wikiRoute(space, parentPath || undefined));
      await loadPages();
    } catch (err) {
      toast(apiErrorMessage(err, 'Delete failed'));
    }
  }

  const membershipProjectId = space.kind === 'project' && space.projectId ? space.projectId : undefined;

  return (
    <AppLayout>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <WorkPageError error={error} projectId={membershipProjectId} onJoined={() => void loadPages()} />
        {error ? null : !path ? (
          pagesLoading ? (
            <LoadingList />
          ) : (
            <SpaceHome
              space={space}
              spaceName={spaceName}
              pages={pages}
              members={members}
            />
          )
        ) : pageLoading || !page ? (
          <LoadingList rows={3} />
        ) : editing ? (
          <WikiPageEditor
            key={`${page.id}:${page.version}:${restore?.version ?? 'live'}`}
            space={space}
            spaceName={spaceName}
            page={page}
            restore={restore}
            confirm={confirm}
            toast={toast}
            onSaved={(saved) => {
              setPage(saved);
              setRestore(null);
              navigate(wikiRoute(space, saved.path));
              void loadPages();
            }}
            onClose={() => navigate(wikiRoute(space, page.path))}
          />
        ) : (
          <WikiPageView
            space={space}
            spaceName={spaceName}
            page={page}
            onEdit={() => navigate(`${wikiRoute(space, page.path)}?edit=1`)}
            onCopyLink={() => void copyLink()}
            onMove={() => setMoveOpen(true)}
            onCreateChild={() => openCreate(byId.get(page.id) ?? null)}
            onHistory={() => setHistoryOpen(true)}
            onDelete={() => void removePage()}
          />
        )}
      </div>

      <CreatePageDialog
        open={createParent.open}
        onOpenChange={(open) => (open ? setCreateParent((c) => ({ ...c, open: true })) : closeCreate())}
        space={space}
        spaceName={spaceName}
        parent={createParent.parent}
        siblings={siblingsOf(createParent.parent?.id ?? null)}
        onCreated={(created) => {
          void loadPages();
          navigate(`${wikiRoute(space, created.path)}?edit=1`);
        }}
      />
      {page ? (
        <>
          <MovePageDialog
            open={moveOpen}
            onOpenChange={setMoveOpen}
            space={space}
            spaceName={spaceName}
            page={page}
            pages={pages}
            onMoved={(moved) => {
              toast('Page moved');
              setPage(moved);
              void loadPages();
              navigate(wikiRoute(space, moved.path));
            }}
          />
          <HistorySheet
            open={historyOpen}
            onOpenChange={setHistoryOpen}
            space={space}
            page={page}
            onRestore={(revision) => {
              setHistoryOpen(false);
              setRestore(revision);
              navigate(`${wikiRoute(space, page.path)}?edit=1`);
            }}
          />
        </>
      ) : null}
    </AppLayout>
  );
}

function SpaceHome({
  space,
  spaceName,
  pages,
  members,
}: {
  space: WikiSpaceRef;
  spaceName: string;
  pages: WikiPageSlim[];
  members: Map<string, MemberDto>;
}) {
  const filters = useDirectoryQuery(SPACE_CHIPS, SPACE_DEFAULT_ORDER);
  const visible = useMemo(() => {
    const rows = pages.filter((row) => matchesQuery(filters.query, [row.title, row.path]));
    const orderBy = filters.orderBy ?? SPACE_DEFAULT_ORDER;
    return rows.slice().sort((left, right) => {
      if (orderBy === 'title') return compareText(left.title, right.title);
      if (orderBy === '-title') return compareText(right.title, left.title);
      return right.updated_at.localeCompare(left.updated_at);
    });
  }, [filters.orderBy, filters.query, pages]);
  const latest = pages.slice().sort((a, b) => b.updated_at.localeCompare(a.updated_at))[0];

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-4 px-2 py-2 sm:px-6 sm:py-4">
      <div>
        <h1 className="text-[22px] font-semibold leading-7 tracking-tight">{spaceName}</h1>
        <p className="mt-1 text-xs text-muted-foreground">
          {pages.length} page{pages.length === 1 ? '' : 's'}
          {latest ? ` · updated ${formatTimestampDateTime(latest.updated_at) ?? ''}` : ''}
        </p>
      </div>
      {pages.length > 0 ? (
        <ListFilterBar
          query={filters.query}
          onQueryChange={(query) => filters.apply({ query })}
          searchPlaceholder="Search pages..."
          searchAriaLabel="Search pages"
          sort={{
            value: filters.orderBy ?? SPACE_DEFAULT_ORDER,
            options: [...SPACE_SORT_OPTIONS],
            onChange: (value) => filters.apply({ orderBy: value }),
          }}
          onClear={filters.clear}
        />
      ) : null}
      {pages.length === 0 ? (
        <BrandEmpty title="No pages yet." />
      ) : visible.length === 0 ? (
        <ListEmpty
          title="No pages match"
          description="Clear filters if you expected matches."
          action={
            <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
              Clear filters
            </Button>
          }
        />
      ) : (
        <PageTable space={space} members={members} pages={visible} />
      )}
    </div>
  );
}

function PageTable({
  space,
  members,
  pages,
}: {
  space: WikiSpaceRef;
  members: Map<string, MemberDto>;
  pages: WikiPageSlim[];
}) {
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow>
          <TableHead className="min-w-0">Title</TableHead>
          <TableHead className="w-44">By</TableHead>
          <TableHead className="w-48">Updated</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {pages.map((row) => (
          <TableRow key={row.id}>
            <TableCell className="min-w-0">
              <Link to={wikiRoute(space, row.path)} className="font-medium hover:underline">
                <span className="truncate">{row.title}</span>
              </Link>
              <span className="block truncate text-[11px] text-muted-foreground">{row.path}</span>
            </TableCell>
            <TableCell>
              <MemberByline members={members} userId={row.updated_by_id} className="text-muted-foreground" />
            </TableCell>
            <TableCell className="text-muted-foreground">
              {formatTimestampDateTime(row.updated_at) ?? row.updated_at}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
